/**
 * 🎓 Guild Skill Tower — Per-Member Skill Purchases (canon Feb 27 2026)
 *
 * Background: BLACK (owner directive).
 *
 * Each member spends their own Chits to push 7 skills:
 *   💪 Power · 🧠 Intellect · 🛡️ Defense · ❤️ Stamina · 🪨 Block · ✨ Charisma · 💰 Gold
 *
 * Block + Charisma require Skill Tower Lv 6+ (advanced bracket).
 * Block is class-scaled: defensive heroes M=1.5 · balanced M=1.0 · glass-cannon M=0.6.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString() : "—");


function DepositWealthPanel({ state, onDeposited }) {
  const [chits, setChits] = useState(0);
  const [od, setOd] = useState(0);
  const [busy, setBusy] = useState(false);
  // 🔄 Restore-absorbed-wealth recovery (Feb 2026 hotfix one-time UI)
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreAmount, setRestoreAmount] = useState(0);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreDone, setRestoreDone] = useState(false);
  const chitsPerWealth = state.conversion?.chits_per_wealth || 1000;
  const odPerWealth = state.conversion?.od_per_wealth || 5;

  const preview = Math.floor(chits / chitsPerWealth) + Math.floor(od / odPerWealth);
  const valid = preview > 0 &&
                chits >= 0 && od >= 0 &&
                chits <= (state.your_chits || 0) &&
                od <= (state.your_od || 0) &&
                chits % chitsPerWealth === 0 &&
                od % odPerWealth === 0;

  const handleDeposit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/personal-wealth/deposit`, { chits, od });
      toast.success(`+${preview} 💎 Personal Wealth`);
      setChits(0); setOd(0);
      await onDeposited?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deposit failed.");
    } finally { setBusy(false); }
  };

  const handleRestore = async () => {
    if (restoreBusy || restoreDone) return;
    const amt = parseInt(restoreAmount || 0, 10);
    if (!amt || amt <= 0) {
      toast.error("Enter the amount of Wealth you remember losing.");
      return;
    }
    if (!window.confirm(
      `Pull ${amt.toLocaleString()} 💎 from the guild pool back into your personal wealth?\n\n` +
      `This is a ONE-TIME recovery for wealth wrongly absorbed by the deprecated migration sweep.`
    )) return;
    setRestoreBusy(true);
    try {
      await axios.post(`${API}/personal-wealth/restore-absorbed`, { amount: amt });
      toast.success(`🔄 Restored ${amt.toLocaleString()} 💎 to your personal wealth.`);
      setRestoreDone(true);
      setRestoreOpen(false);
      await onDeposited?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Restore failed.";
      toast.error(msg);
      // If the server says "already restored", lock the panel
      if (/already restored/i.test(msg)) setRestoreDone(true);
    } finally { setRestoreBusy(false); }
  };

  const stepChits = (delta) => setChits((c) => Math.max(0, Math.min((state.your_chits || 0), c + delta)));
  const stepOd = (delta) => setOd((o) => Math.max(0, Math.min((state.your_od || 0), o + delta)));

  return (
    <div className="rounded-xl border border-emerald-700/40 bg-emerald-950/20 p-3 mb-4" data-testid="deposit-wealth-panel">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold">💎 Convert to Personal Wealth</div>
        <div className="text-[10px] text-neutral-500">
          {chitsPerWealth.toLocaleString()} 🪙 = 1 💎 · {odPerWealth} 💸 = 1 💎
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-neutral-400 uppercase tracking-wide">Chits 🪙</label>
          <div className="flex items-center gap-1 mt-1">
            <button type="button" onClick={() => stepChits(-chitsPerWealth)} className="px-2 py-1 bg-black/60 border border-neutral-700 rounded text-xs">−</button>
            <input
              type="number" min="0" step={chitsPerWealth}
              value={chits}
              onChange={(e) => setChits(Math.max(0, parseInt(e.target.value || "0", 10)))}
              className="flex-1 bg-black/60 border border-neutral-700 rounded px-2 py-1 text-sm text-amber-200 font-mono"
              data-testid="deposit-chits-input"
            />
            <button type="button" onClick={() => stepChits(chitsPerWealth)} className="px-2 py-1 bg-black/60 border border-neutral-700 rounded text-xs">+</button>
          </div>
        </div>
        <div>
          <label className="text-[10px] text-neutral-400 uppercase tracking-wide">OD 💸</label>
          <div className="flex items-center gap-1 mt-1">
            <button type="button" onClick={() => stepOd(-odPerWealth)} className="px-2 py-1 bg-black/60 border border-neutral-700 rounded text-xs">−</button>
            <input
              type="number" min="0" step={odPerWealth}
              value={od}
              onChange={(e) => setOd(Math.max(0, parseInt(e.target.value || "0", 10)))}
              className="flex-1 bg-black/60 border border-neutral-700 rounded px-2 py-1 text-sm text-pink-200 font-mono"
              data-testid="deposit-od-input"
            />
            <button type="button" onClick={() => stepOd(odPerWealth)} className="px-2 py-1 bg-black/60 border border-neutral-700 rounded text-xs">+</button>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDeposit}
        disabled={!valid || busy}
        data-testid="deposit-wealth-btn"
        className={`mt-2 w-full rounded-md py-2 text-xs font-black tracking-wider uppercase transition-all ${
          valid && !busy
            ? "bg-emerald-500 text-black hover:brightness-110 active:scale-[0.98]"
            : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
        }`}
      >
        {busy ? "⏳ Depositing…" : preview > 0 ? `Deposit · +${preview} 💎` : "Pick an amount (multiples only)"}
      </button>

      {/* 🔄 Legacy-absorb recovery — one-time use per account, server-guarded */}
      {!restoreDone && (
        <div className="mt-3 pt-3 border-t border-emerald-900/40">
          {!restoreOpen ? (
            <button
              type="button"
              onClick={() => setRestoreOpen(true)}
              data-testid="open-restore-absorbed-btn"
              className="w-full text-[10px] uppercase tracking-widest text-amber-400/80 hover:text-amber-300 underline underline-offset-2"
            >
              🔄 Was your personal wealth absorbed by mistake? Restore it →
            </button>
          ) : (
            <div className="rounded-lg border border-amber-700/40 bg-amber-950/10 p-3" data-testid="restore-absorbed-panel">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">
                🔄 Restore Absorbed Wealth (one-time)
              </div>
              <p className="text-[10px] text-neutral-400 mb-2 leading-snug">
                A legacy migration sweep accidentally vacuumed personal Wealth into the guild pool on every upgrade attempt. Enter the amount you remember losing — it&apos;ll be pulled back from the pool to your personal balance. <span className="text-amber-300 font-bold">One use only per account.</span>
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="1" step="100"
                  value={restoreAmount || ""}
                  onChange={(e) => setRestoreAmount(Math.max(0, parseInt(e.target.value || "0", 10)))}
                  placeholder="e.g. 3000"
                  className="flex-1 bg-black/60 border border-amber-700/40 rounded px-2 py-1 text-sm text-amber-200 font-mono"
                  data-testid="restore-absorbed-amount-input"
                />
                <button
                  type="button"
                  onClick={handleRestore}
                  disabled={restoreBusy || !restoreAmount || restoreAmount <= 0}
                  data-testid="restore-absorbed-btn"
                  className={`px-3 py-1.5 text-xs font-black tracking-wider uppercase rounded transition-all ${
                    !restoreBusy && restoreAmount > 0
                      ? "bg-amber-500 text-black hover:brightness-110 active:scale-[0.98]"
                      : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                  }`}
                >
                  {restoreBusy ? "⏳" : "Restore"}
                </button>
                <button
                  type="button"
                  onClick={() => { setRestoreOpen(false); setRestoreAmount(0); }}
                  className="px-2 py-1.5 text-xs text-neutral-500 hover:text-neutral-300"
                  data-testid="restore-absorbed-cancel-btn"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SkillRow({ skillId, spec, perSkill, yourWealth, onBuy, busySkill }) {
  const currentLv = perSkill?.current_level ?? 0;
  const cap = perSkill?.cap ?? 0;
  const nextCost = perSkill?.next_cost_wealth ?? 0;
  const locked = perSkill?.locked ?? false;
  const atCap = currentLv >= cap && cap > 0;
  const canAfford = nextCost > 0 && yourWealth >= nextCost;
  const isBusy = busySkill === skillId;
  const canBuy = !locked && !atCap && canAfford && !isBusy && nextCost > 0;

  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${
        locked ? "border-neutral-800 bg-black/60 opacity-60" :
        atCap ? "border-fuchsia-700/60 bg-fuchsia-950/20" :
        canAfford ? "border-emerald-700/50 bg-emerald-950/10" :
        "border-neutral-700/50 bg-black/40"
      }`}
      data-testid={`skill-row-${skillId}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="text-4xl shrink-0">{spec.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-base font-black text-amber-200 tracking-wide">{spec.name}</h3>
            {locked && <span className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">🔒 Tower Lv 6+</span>}
            {atCap && <span className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">⭐ AT CAP</span>}
          </div>
          <p className="text-[11px] text-neutral-400 mt-1">{spec.blurb}</p>
        </div>
      </div>

      {/* Stat math summary */}
      <div className="rounded bg-black/40 border border-neutral-800 px-3 py-2 mb-3 text-[10px]">
        <div className="flex items-center justify-between text-neutral-400">
          <span>Per level:</span>
          <span className="text-amber-200 font-bold">{spec.net_per_lv}</span>
        </div>
        <div className="flex items-center justify-between text-neutral-500 mt-0.5">
          <span>Math:</span>
          <span className="font-mono text-right truncate ml-2 max-w-[70%]">{spec.sub_math}</span>
        </div>
      </div>

      {/* Level meter */}
      <div className="mb-3">
        <div className="flex items-baseline justify-between text-[10px] mb-1">
          <span className="text-neutral-500 uppercase tracking-widest">Your level</span>
          <span className="text-amber-200 font-mono font-bold">
            Lv <span className="text-2xl" data-testid={`skill-row-${skillId}-level`}>{currentLv}</span>
            <span className="text-neutral-500"> / {cap || 0}</span>
          </span>
        </div>
        <div className="h-2 bg-black border border-neutral-800 rounded overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-700 to-amber-400 transition-all"
            style={{ width: cap > 0 ? `${(currentLv / cap) * 100}%` : "0%" }}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => canBuy && onBuy?.(skillId, nextCost)}
        disabled={!canBuy}
        data-testid={`skill-row-${skillId}-buy-btn`}
        className={`w-full rounded-md py-2.5 text-xs font-black tracking-wider uppercase transition-all ${
          canBuy
            ? "bg-amber-500 text-black hover:brightness-110 active:scale-[0.98]"
            : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
        }`}
      >
        {isBusy ? "⏳ Buying…" :
         locked ? "🔒 Tower Lv 6+ Required" :
         atCap ? "⭐ At Tower Cap — Upgrade Tower to push higher" :
         !canAfford ? `Need ${fmt(nextCost)} 💎 (you have ${fmt(yourWealth)})` :
         `⬆ Buy Lv ${currentLv + 1} · ${fmt(nextCost)} 💎 Wealth`}
      </button>
    </div>
  );
}


export default function GuildSkillsPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busySkill, setBusySkill] = useState(null);

  const fetchState = async () => {
    const r = await axios.get(`${API}/guild/skills/state`);
    setState(r.data || null);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await fetchState();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load guild skills.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleBuy = async (skillId, cost) => {
    if (!window.confirm(`Spend ${cost.toLocaleString()} 🪙 Chits to buy +1 ${state.skills_catalog[skillId].name}?`)) return;
    setBusySkill(skillId);
    try {
      const r = await axios.post(`${API}/guild/skills/buy`, { skill_id: skillId });
      setState(r.data || null);
      toast.success(`🎓 ${state.skills_catalog[skillId].name} → Lv ${r.data?.per_skill?.[skillId]?.current_level ?? "?"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
      try { await fetchState(); } catch { /* noop */ }
    } finally {
      setBusySkill(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-amber-200 flex items-center justify-center" data-testid="guild-skills-loading">
        <div className="text-sm tracking-widest opacity-60">⏳ Loading skill tower…</div>
      </div>
    );
  }

  if (!state?.in_guild) {
    return (
      <div className="min-h-screen bg-black text-neutral-200 p-6" data-testid="guild-skills-no-guild">
        <Link to="/guild" className="text-[11px] text-neutral-500 hover:text-amber-300 tracking-widest uppercase">← Back</Link>
        <h1 className="text-3xl font-black text-amber-200 tracking-wide mt-2">🎓 Skill Tower</h1>
        <p className="text-sm text-neutral-400 mt-3">
          Join or found a guild to access the Skill Tower. Each member trains their own
          stats from their personal Wealth (💎) balance — gated by the guild&apos;s Skill Tower level.
        </p>
      </div>
    );
  }

  const catalog = state.skills_catalog || {};
  const perSkill = state.per_skill || {};
  const order = [
    "power", "intellect", "defense", "stamina",
    "block", "charisma", "crit", "piercing", "status_resist", "gold",
  ];

  return (
    <div className="min-h-screen bg-black text-neutral-200 pb-32" data-testid="guild-skills-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <Link to="/guild/hall" className="text-[11px] text-neutral-500 hover:text-amber-300 tracking-widest uppercase">
              ← Back to Guild Hall
            </Link>
            <h1 className="text-3xl sm:text-4xl font-black text-amber-200 tracking-wide mt-1">
              🎓 Skill Tower
            </h1>
            <p className="text-xs text-neutral-500 mt-1">
              Tower Lv <span className="text-cyan-300 font-bold" data-testid="skills-tower-level">{state.tower_level}</span> ·
              your skill caps move with the Tower&apos;s level.
            </p>
          </div>

          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Personal Wealth</div>
            <div className="text-2xl font-black text-emerald-300" data-testid="skills-your-wealth">
              {fmt(state.your_wealth)} 💎
            </div>
            <div className="text-[9px] text-neutral-500 mt-0.5">
              Chits: {fmt(state.your_chits)} 🪙 · OD: {fmt(state.your_od)} 💸
            </div>
            {state.your_class_id && (
              <div className="text-[10px] text-neutral-500 mt-1">
                Class M for Block: <span className="text-cyan-300 font-bold">×{state.block_modifier}</span>
                <div className="text-[9px] text-neutral-600">
                  {state.block_modifier >= 1.5 ? "🛡️ Defensive frontline" :
                   state.block_modifier <= 0.6 ? "🗡️ Pure glass-cannon" :
                   "⚖️ Balanced / support"}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 💎 Deposit panel — convert Chits/OD → Personal Wealth */}
        <DepositWealthPanel state={state} onDeposited={fetchState} />

        {/* Skills grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {order.map((sid) => (
            <SkillRow
              key={sid}
              skillId={sid}
              spec={catalog[sid] || {}}
              perSkill={perSkill[sid]}
              yourWealth={state.your_wealth}
              onBuy={handleBuy}
              busySkill={busySkill}
            />
          ))}
        </div>

        <div className="mt-8 text-center text-[10px] text-neutral-600 italic">
          Stat math is canonical · Block is class-scaled (defensive M=1.5 · balanced M=1.0 · glass-cannon M=0.6) · Costs paid from your Personal Wealth (deposit Chits/OD to top it up)
        </div>
      </div>
    </div>
  );
}
